import { Box, Button, ImageList, ImageListItem, Paper, Typography, useTheme } from "@mui/material";
import DOMPurify from "dompurify";
import { tokens } from "../../../theme";
import {
  CTABlockValue,
  GalleryBlockValue,
  ImageBlockValue,
  QuoteBlockValue,
  StreamBlock,
  VideoEmbedBlockValue,
} from "../types";

// Rich text ships as raw HTML from Wagtail; always sanitize before injecting.
const sanitize = (html: string) => DOMPurify.sanitize(html);

// Embeds are server-restricted to YouTube/Vimeo, so allow the iframe through.
const sanitizeEmbed = (html: string) =>
  DOMPurify.sanitize(html, {
    ADD_TAGS: ["iframe"],
    ADD_ATTR: [
      "allow",
      "allowfullscreen",
      "frameborder",
      "scrolling",
      "src",
      "title",
      "width",
      "height",
    ],
  });

function HeadingBlock({ value }: { value: string }) {
  const colors = tokens(useTheme().palette.mode);
  return (
    <Typography variant="h3" sx={{ mt: 4, mb: 2, fontWeight: 700, color: colors.primary[700] }}>
      {value}
    </Typography>
  );
}

function RichTextBlock({ value }: { value: string }) {
  const colors = tokens(useTheme().palette.mode);
  return (
    <Box
      sx={{
        color: colors.primary[700],
        lineHeight: 1.8,
        fontSize: "1.05rem",
        "& a": { color: colors.primary[400], textDecoration: "underline" },
        "& h2, & h3, & h4": { color: colors.primary[700], mt: 3, mb: 1.5 },
        "& img": { maxWidth: "100%", borderRadius: 2 },
        "& ul, & ol": { pl: 3 },
      }}
      dangerouslySetInnerHTML={{ __html: sanitize(value) }}
    />
  );
}

function ImageBlock({ value }: { value: ImageBlockValue }) {
  return (
    <Box
      component="img"
      src={value.url}
      alt={value.alt}
      loading="lazy"
      sx={{ width: "100%", height: "auto", borderRadius: 2, my: 3, display: "block" }}
    />
  );
}

function GalleryBlock({ value }: { value: GalleryBlockValue }) {
  const colors = tokens(useTheme().palette.mode);
  if (!value.images.length) return null;
  return (
    <Box sx={{ my: 3 }}>
      {value.title && (
        <Typography variant="h6" sx={{ mb: 1.5, color: colors.primary[700] }}>
          {value.title}
        </Typography>
      )}
      <ImageList variant="masonry" cols={2} gap={12}>
        {value.images.map((img, i) => (
          <ImageListItem key={`${img.url}-${i}`}>
            <Box
              component="img"
              src={img.url}
              alt={img.alt}
              loading="lazy"
              sx={{ width: "100%", borderRadius: 2 }}
            />
            {img.caption && (
              <Typography variant="caption" sx={{ color: colors.primary[300], mt: 0.5 }}>
                {img.caption}
              </Typography>
            )}
          </ImageListItem>
        ))}
      </ImageList>
    </Box>
  );
}

function VideoEmbedBlock({ value }: { value: VideoEmbedBlockValue }) {
  if (!value.html) {
    return null;
  }
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        pt: "56.25%", // 16:9
        my: 3,
        borderRadius: 2,
        overflow: "hidden",
        "& iframe": { position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 },
      }}
      dangerouslySetInnerHTML={{ __html: sanitizeEmbed(value.html) }}
    />
  );
}

function QuoteBlock({ value }: { value: QuoteBlockValue }) {
  const colors = tokens(useTheme().palette.mode);
  return (
    <Box
      component="blockquote"
      sx={{
        my: 3,
        ml: 0,
        pl: 3,
        py: 1,
        borderLeft: `4px solid ${colors.primary[400]}`,
        fontStyle: "italic",
      }}
    >
      <Typography sx={{ fontSize: "1.25rem", color: colors.primary[700] }}>
        {value.quote}
      </Typography>
      {value.attribution && (
        <Typography variant="body2" sx={{ mt: 1, color: colors.primary[300] }}>
          — {value.attribution}
        </Typography>
      )}
    </Box>
  );
}

function CTABlock({ value }: { value: CTABlockValue }) {
  const colors = tokens(useTheme().palette.mode);
  return (
    <Paper
      elevation={0}
      sx={{
        my: 4,
        p: 4,
        textAlign: "center",
        borderRadius: 3,
        backgroundColor: colors.primary[600],
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 700, color: colors.primary[400], mb: 1 }}>
        {value.heading}
      </Typography>
      {value.text && (
        <Typography sx={{ color: colors.primary[700], mb: 2 }}>{value.text}</Typography>
      )}
      <Button variant="contained" color="primary" href={value.button_url}>
        {value.button_text}
      </Button>
    </Paper>
  );
}

function Block({ block }: { block: StreamBlock }) {
  switch (block.type) {
    case "heading":
      return <HeadingBlock value={block.value} />;
    case "rich_text":
      return <RichTextBlock value={block.value} />;
    case "image":
      return <ImageBlock value={block.value} />;
    case "gallery":
      return <GalleryBlock value={block.value} />;
    case "video_embed":
      return <VideoEmbedBlock value={block.value} />;
    case "quote":
      return <QuoteBlock value={block.value} />;
    case "cta":
      return <CTABlock value={block.value} />;
    default:
      // Forward-compatible: unknown block types render nothing.
      return null;
  }
}

export default function StreamFieldRenderer({ body }: { body: StreamBlock[] }) {
  return (
    <>
      {body.map((block) => (
        <Block key={block.id} block={block} />
      ))}
    </>
  );
}
